/** 连接设计师图片面板与 Canvas 变形会话，管理加载遮罩和确认、取消监听 */
import { useCallback, useEffect, useRef } from 'react';
import message from '@meitu/saas-ui/es/message';
import type { EDeformationImgType } from '@meitu/saas-editor-core/es/plugins/apps/ai-photo-editor/deformation-img/states/deformation-img';
import { useLocaleStore } from '@/i18n';
import { editor } from '@/instance';
import { useEditorModel } from '@/store/models/editor-model';

/**
 * 在图片主面板挂载变形入口，依赖当前图片 ID、编辑器实例和应用禁用状态
 *
 * @param layerId - 图片主面板当前目标图层 ID
 * @returns 打开透视或扭曲预览的回调
 *
 * 副作用：显示加载和失败提示，确认时保存结果，会话结束或卸载时恢复面板并清理监听。
 */
export const useDeformationImage = (layerId: string | null | undefined) => {
    const { $t } = useLocaleStore();
    const pending = useRef(false);
    const cleanupRef = useRef<(() => void) | null>(null);
    const mounted = useRef(true);
    const operationGeneration = useRef(0);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            operationGeneration.current += 1;
            cleanupRef.current?.();
            if (pending.current) {
                editor.deformationImg.close();
                pending.current = false;
                editor.services.mask.hide();
                useEditorModel.getState().setIsDisable(false);
            }
        };
    }, []);

    return useCallback(
        async (type: EDeformationImgType) => {
            if (!layerId || pending.current || useEditorModel.getState().isDisable) return;
            const generation = ++operationGeneration.current;
            const isCurrent = () => mounted.current && generation === operationGeneration.current;
            pending.current = true;
            useEditorModel.getState().setIsDisable(true);
            editor.services.mask.show({ loading: true });
            const finish = () => {
                if (!isCurrent()) return;
                operationGeneration.current += 1;
                cleanupRef.current?.();
                cleanupRef.current = null;
                pending.current = false;
                editor.services.mask.hide();
                useEditorModel.getState().setIsDisable(false);
            };
            // 先监听再加载预览，确保初始化失败或模板切换都能恢复面板状态。
            const offClose = editor.deformationImg.onClose(finish);
            const offError = editor.deformationImg.onError((error, shouldClose) => {
                if (!isCurrent()) return;
                console.error('Deformation preview failed', error);
                message.error(
                    $t((v) => (shouldClose ? v.extensions.refreshAndTryAgain : v.panel.productRetouchingPanel.failed)),
                );
                if (shouldClose) editor.deformationImg.close();
            });
            const offConfirm = editor.deformationImg.onConfirm(async (image, offset) => {
                if (!isCurrent()) return;
                editor.services.mask.show({ loading: true });
                try {
                    const saved = await editor.deformationImg.save(image, undefined, offset);
                    if (isCurrent() && (saved || !editor.deformationImg.getCurrentLayer())) finish();
                } catch (error) {
                    console.error('Save deformation image failed', error);
                    if (isCurrent()) message.error($t((v) => v.editor.aiPoster.savingErrorMessage));
                } finally {
                    if (isCurrent()) editor.services.mask.hide();
                }
            });
            const offCancel = editor.deformationImg.onCancel(() => {
                if (isCurrent()) editor.deformationImg.close();
            });
            cleanupRef.current = () => {
                offConfirm();
                offCancel();
                offClose();
                offError();
            };
            try {
                const opened = await editor.deformationImg.openForLayer(type, layerId);
                if (isCurrent() && !opened) finish();
            } catch (error) {
                console.error('Open deformation image failed', error);
                if (isCurrent()) {
                    message.error($t((v) => v.extensions.refreshAndTryAgain));
                    finish();
                }
            } finally {
                if (isCurrent()) editor.services.mask.hide();
            }
        },
        [layerId, $t],
    );
};
